// wijmo
import * as wjcCore from "@grapecity/wijmo"
import * as wjcGrid from "@grapecity/wijmo.grid"
import * as wjcInput from "@grapecity/wijmo.input"
import CONSTANT from "../../constant"
import { 全部所車番検索Enabled } from "../../constant/featureFlags"
import ZyomuinService from "../../service/driver/zyomuinService"
import HinmeiService from "../../service/master/hinmei/hinmeiService"
import { THinmeiListGetReq } from "../../service/master/hinmei/THinmei"
import NinusiService from "../../service/master/ninusi/ninusiService"
import TumiorosisakiService from "../../service/master/tumiorosisaki/tumiorosisakiService"
import { T傭車マスタ } from "../../service/master/yosya/TYosya"
import YosyaService from "../../service/master/yosya/yosyaService"
import SyaryoService from "../../service/truck/syaryoService"
import {
  TSyaryoListGetReq,
  TSyaryoModalList,
} from "../../service/truck/TSyaryo"
import { T傭車DataMap用Item, T車輌DataMap用Item } from "../../type/gridDataMap"
import commonUtil from "./commonUtil"

// 表示用string生成
export const 車輌DisplayNameFunc = (d: TSyaryoModalList) =>
  commonUtil.addTextWithStartDelimiter(`${d.車番}:`, [
    d.車種区分名称1,
    d.部所名,
    d.社員氏名,
    d.トレーラ車番,
  ])

// 全車輌リストからdisplayNameを生成する。同一displayNameが複数存在する場合は車輌IDを末尾に付加して識別できるようにする（KAIROS-1108）
export const build車輌DataMapList = (list: TSyaryoModalList[]): T車輌DataMap用Item[] => {
  const withName = list.map((d) => ({ ...d, displayName: 車輌DisplayNameFunc(d) }))
  const counts = new Map<string, number>()
  withName.forEach((d) => counts.set(d.displayName, (counts.get(d.displayName) ?? 0) + 1))
  return withName.map((d) =>
    (counts.get(d.displayName) ?? 0) > 1
      ? { ...d, displayName: `${d.displayName} (${d.車輌ID})` }
      : d
  )
}

export const 傭車DisplayNameFunc = (d: T傭車マスタ) =>
  commonUtil.addTextWithStartDelimiter(`${d.車番}:`, [
    d.車種区分名称1,
    d.傭車先略称,
    d.乗務員名,
  ])

// grid上のAutoComplete用のitemsSourceFunction等を定義するクラスです

/**
 * ロストフォーカス時の挙動を定義します。
 * フォーカスアウト時にドロップダウンが出ていた場合に1件目を取得する挙動を追加します
 * @param control autocomplete
 * @param preText 入力前の文字列 比較用に使用します。
 * @param setDataFunction 取得したデータを操作する関数
 * @param setDataEmptyFunction データを取得できなかった場合に操作する関数
 * @returns 各データ処理関数の返却値 or undefined
 */
export const abstractSelectItemWhenLostFocusHandler = <T>(
  control: wjcInput.AutoComplete,
  setDataFunction: (value: T, control?: wjcInput.AutoComplete) => unknown,
  setDataEmptyFunction?: (control?: wjcInput.AutoComplete) => unknown
) => {
  // 選択済アイテム
  if (control.selectedItem) {
    return setDataFunction(control.selectedItem, control)
  } else {
    const itemsSource: T[] = control?.collectionView?.sourceCollection ?? []
    if (itemsSource.length === 1) {
      // 1つしかアイテムがない場合、先頭を取得し埋め込む
      const items: T = itemsSource[0]
      return setDataFunction(items, control)
    } else {
      return setDataEmptyFunction ? setDataEmptyFunction(control) : undefined
    }
  }
}

/**
 * 基本的なdataMapを利用したitemsSourceFunction
 * AutoCompleteにする場合、column側にも同じdataMapを設定してください
 * @param query
 * @param max
 * @param callback
 * @param dataMap
 * @param selectedValuePath
 * @param startWith
 * @returns
 */
export const itemsSourceFunctionSearchOuterDataMap = <T>(
  query: string,
  max: number,
  callback: wjcInput.IGetItemsCallback,
  dataMap: wjcGrid.DataMap | undefined,
  selectedValuePath: keyof T,
  startWith?: boolean
) => {
  if (dataMap == null) {
    callback([])
    return
  }
  const sourceCollection: T[] = dataMap.collectionView.sourceCollection
  if (sourceCollection == null) {
    callback([])
    return
  }
  callback(
    sourceCollection.filter((s) => {
      let selectedValue =
        s[selectedValuePath] != null
          ? (s[selectedValuePath] as unknown as string)
          : null
      if (
        s[selectedValuePath] instanceof Object ||
        s[selectedValuePath] instanceof Array
      ) {
        return false
      }
      if (selectedValue == null) return false
      if (startWith) {
        return selectedValue.startsWith(query)
      } else {
        return selectedValue.includes(query)
      }
    })
  )
  return
}

/**
 * get車輌itemsSourceFunctionByDataMapのInnerです
 * @param query
 * @param max
 * @param callback
 * @param 原動機有無フラグ
 * @param dataMap
 * @param 運行部所コード
 * @returns
 */
export const get車輌itemsSourceFunctionByDataMapInner = (
  query: string,
  max: number,
  callback: wjcInput.IGetItemsCallback,
  原動機有無フラグ: 0 | 1 | undefined,
  dataMap: wjcGrid.DataMap | undefined,
  運行部所コード: string | undefined
) => {
  if (dataMap == null) {
    callback([])
    return
  }
  const sourceCollection: T車輌DataMap用Item[] =
    dataMap.collectionView.sourceCollection
  if (sourceCollection == null) {
    callback([])
    return
  }
  let items: T車輌DataMap用Item[]
  if (query == null || query === "") {
    items = sourceCollection.filter(
      (d) =>
        (全部所車番検索Enabled || d.部所コード === 運行部所コード) &&
        (原動機有無フラグ != null
          ? 原動機有無フラグ === d.原動機有無フラグ
          : true)
    )
  } else {
    items = sourceCollection.filter((d) => {
      return (
        d.車番 != null &&
        d.車番.startsWith(query) &&
        (全部所車番検索Enabled || d.部所コード === 運行部所コード) &&
        (原動機有無フラグ != null
          ? 原動機有無フラグ === d.原動機有無フラグ
          : true)
      )
    })
  }
  callback(items)
  return
}

/**
 * 車輌用のItemsSource（DataMap取得）
 * AutoCompleteにする場合、column側にも同じdataMapを設定してください
 * @param query
 * @param max
 * @param callback
 * @param 原動機有無フラグ
 * @param dataMap
 * @param editedRowData
 * @returns
 */
export const get車輌itemsSourceFunctionByDataMap = <
  TGridRow extends { 運行部所コード?: string }
>(
  query: string,
  max: number,
  callback: wjcInput.IGetItemsCallback,
  原動機有無フラグ: 0 | 1 | undefined,
  dataMap: wjcGrid.DataMap | undefined,
  editedRowData: React.MutableRefObject<TGridRow | undefined>
) =>
  get車輌itemsSourceFunctionByDataMapInner(
    query,
    max,
    callback,
    原動機有無フラグ,
    dataMap,
    editedRowData?.current?.運行部所コード
  )

/**
 * 海コン用車輌用のItemsSource（DataMap取得）
 *
 * AutoCompleteにする場合、column側にも同じdataMapを設定してください
 * @param query
 * @param max
 * @param callback
 * @param 原動機有無フラグ
 * @param dataMap
 * @param editedRowData
 * @returns
 */
export const get海コン一覧用車輌itemsSourceFunctionByDataMap = (
  query: string,
  max: number,
  callback: wjcInput.IGetItemsCallback,
  原動機有無フラグ: 0 | 1 | undefined,
  dataMap: wjcGrid.DataMap | undefined,
  運行部所コード: string | undefined
) =>
  get車輌itemsSourceFunctionByDataMapInner(
    query,
    max,
    callback,
    原動機有無フラグ,
    dataMap,
    運行部所コード
  )

/**
 * 車輌用のItemsSource（API取得）
 *
 * @param query
 * @param max
 * @param callback
 * @param 原動機有無フラグ
 * @param dataMap
 * @param editedRowData
 * @returns
 */
export const get車輌itemsSourceFunctionByApi = (
  query: string,
  max: number,
  callback: wjcInput.IGetItemsCallback,
  原動機有無フラグ: 0 | 1 | undefined,
  部所コード: string | undefined,
  otherCondition?: TSyaryoListGetReq
) => {
  if (query == null || wjcCore.isNullOrWhiteSpace(query)) {
    callback([])
    return
  } else {
    const service = new SyaryoService()
    return service
      .getList({
        廃車フラグ: 0,
        原動機有無フラグ,
        車番: query,
        部所コード,
        ...otherCondition,
      })
      .then((res) => {
        const selectItem = res.data ? build車輌DataMapList(res.data) : []
        callback(selectItem)
      })
  }
}

/**
 * 傭車用のItemsSource（DataMap取得）
 *
 * @param query
 * @param max
 * @param callback

 * @returns
 */
export const get傭車itemsSourceFunctionByDataMap =
  (
    dataMap: wjcGrid.DataMap | undefined,
    原動機有無フラグ: 0 | 1 | undefined,
    支払先ID: number | undefined
  ) =>
  (query: string, max: number, callback: wjcInput.IGetItemsCallback) => {
    if (dataMap == null) {
      callback([])
      return
    }
    const sourceCollection: T傭車DataMap用Item[] =
      dataMap.collectionView.sourceCollection
    if (sourceCollection == null) {
      callback([])
      return
    }
    let items: T傭車DataMap用Item[]
    // 空っぽのときは傭車会社のもの全件返却(傭車会社が空の場合そのまま返却)
    if (query == null || query === "") {
      items = sourceCollection.filter(
        (d) =>
          d.支払先ID === 支払先ID && d.原動機有無フラグ === 原動機有無フラグ
      )
    } else {
      items = sourceCollection.filter((d) => {
        return (
          d.車番 != null &&
          d.車番.startsWith(query) &&
          d.支払先ID === 支払先ID &&
          d.原動機有無フラグ === 原動機有無フラグ
        )
      })
    }
    callback(items)
    return
  }

/**
 * 傭車用のItemsSource（API取得）
 *
 * @param query
 * @param max
 * @param callback

 * @returns
 */
export const get傭車itemsSourceFunctionByApi = (
  query: string,
  max: number,
  callback: wjcInput.IGetItemsCallback,
  傭車先コード: string | undefined
) => {
  if (query == null || wjcCore.isNullOrWhiteSpace(query)) {
    callback([])
    return
  } else {
    const service = new YosyaService()
    return service
      .getList({
        車番: query,
        傭車先コード,
      })
      .then((res) => {
        const selectItem = res.data
          ? res.data.map((d) => {
              let displayName = 傭車DisplayNameFunc(d)
              return { ...d, displayName }
            })
          : []

        callback(selectItem)
      })
  }
}

// 荷主検索用(API接続)
export const get荷主ItemsSourceByApi = (
  query: string,
  max: number,
  callback: wjcInput.IGetItemsCallback
) => {
  if (query == null || wjcCore.isNullOrWhiteSpace(query)) {
    callback([])
    return
  } else {
    const service = new NinusiService()
    return service.getList({ 荷主コード: query }).then((res) => {
      const selectItem = res.data
        ? res.data.map((d) => {
            let displayName = `${d.荷主コード ?? ""}`
            let detailText = ``
            detailText = commonUtil.addTextWithStartDelimiter(detailText, [
              d.荷主略称1,
            ])
            if (detailText !== "") {
              displayName += `: ${detailText}`
            }
            return { ...d, displayName }
          })
        : []

      callback(selectItem)
    })
  }
}

// 積卸検索用(API接続)
export const get積卸ItemsSourceByApi = (
  query: string,
  max: number,
  callback: wjcInput.IGetItemsCallback,
  荷主コード: string
) => {
  if (query == null || wjcCore.isNullOrWhiteSpace(query)) {
    callback([])
    return
  } else {
    const service = new TumiorosisakiService()
    return service.getList({ 積卸先コード: query, 荷主コード }).then((res) => {
      const selectItem = res.data
        ? res.data.map((d) => {
            let displayName = `${d.積卸先コード ?? ""}`
            let detailText = ``
            detailText = commonUtil.addTextWithStartDelimiter(detailText, [
              d.積卸先略称,
              d.住所１,
              d.住所２,
            ])
            if (detailText !== "") {
              displayName += `: ${detailText}`
            }
            return { ...d, displayName }
          })
        : []

      callback(selectItem)
    })
  }
}

// 乗務員・同乗者検索用(API接続)
// 退職していないもののみ検索
export const get乗務員ItemsSourceByApi = (
  query: string,
  max: number,
  callback: wjcInput.IGetItemsCallback,
  部所コード: string
) => {
  if (query == null || wjcCore.isNullOrWhiteSpace(query)) {
    callback([])
    return
  } else {
    const service = new ZyomuinService()
    return service
      .getList({ 社員氏名: query, 部所コード, 退職フラグ: 0 })
      .then((res) => {
        const selectItem = res.data
          ? res.data.map((d) => {
              let displayName = `${d.社員氏名 ?? ""}`
              let detailText = ``
              detailText = commonUtil.addTextWithStartDelimiter(detailText, [
                d.社員コード,
                d.部所略称,
              ])
              if (detailText !== "") {
                displayName += `: ${detailText}`
              }
              return { ...d, displayName }
            })
          : []

        callback(selectItem)
      })
  }
}

// 品名検索用(API接続)
// ダミー用のコードのときはダミー荷主に紐づく品名で検索する
export const get品名ItemsSourceByApi = (
  query: string,
  max: number,
  callback: wjcInput.IGetItemsCallback,
  荷主コード: string
) => {
  if (query == null || wjcCore.isNullOrWhiteSpace(query)) {
    callback([])
    return
  } else {
    // 1文字かつダミー荷主用頭文字スタートではない場合、検索を行わない
    // if (
    //   !query.startsWith(CONSTANT.ダミー荷主適用品コード頭文字) &&
    //   query.length <= 1
    // ) {
    //   callback([])
    //   return
    // }
    const service = new HinmeiService()
    // queryの開始文字がダミー荷主対応の時はダミー荷主に紐づく品名を取得する

    const searchCondition: THinmeiListGetReq = {
      品コード: query,
      荷主コード: query.startsWith(CONSTANT.ダミー荷主適用品コード頭文字)
        ? CONSTANT.ダミー荷主コード
        : 荷主コード,
    }
    return service.getList(searchCondition).then((res) => {
      const selectItem = res.data
        ? res.data.map((d) => {
            let displayName = `${d.品コード ?? ""}`
            let detailText = ``
            detailText = commonUtil.addTextWithStartDelimiter(detailText, [
              d.品名,
            ])
            if (detailText !== "") {
              displayName += `: ${detailText}`
            }
            return { ...d, displayName }
          })
        : []

      callback(selectItem)
    })
  }
}
